/**
 * One line about prior work, wherever a novelty number appears.
 *
 * A check measured prior art honestly — "Raise oxidation" re-rated 0.75 → 0.30
 * against a paper that had already done it — and the card went on saying NOVEL 70,
 * the cleared list went on sorting by the self-rating, and neither the report nor
 * anything else said a word. The measurement lived three disclosures deep in the
 * check panel. This is the sentence every surface that prints a novelty now prints
 * beside it:
 *
 *   Closest prior work: <title> (<closeness>) · novelty re-rated 0.75 → 0.30 by
 *   the checking model
 *
 * Four rules, each for a reason:
 *
 *  - The title is the SOURCE's title as the search returned it, found through the
 *    prior-art entry's `ref` among the sources the citation gate kept (invariant
 *    1), or a cited passage's document title and passage number, found the same
 *    way among the record's cites. A ref that names nothing kept names nothing
 *    here. Passages were once left untitled: the live pass keyed them by the
 *    call-local number and the stored cite by the document-level one, so a `p3`
 *    could not be tied to a document with confidence. Both sides now store
 *    `passageRef` (D146), and a wrong title is still worse than none — which is
 *    why this looks the ref up and never parses it.
 *  - The two numbers are printed side by side and never blended. The first is the
 *    model's self-rating, the second the checking model's re-rating after it had
 *    something to look at; `node.novelty` is never written (see `Verification`).
 *    Both on the scale the card's novelty cell prints (`fmtRating`), so the cell
 *    and the "from" beside it are one number printed one way.
 *  - "Nothing close in this search" only after a complete web check that returned
 *    results, lost nothing to a failure or the search limit, and had none of its
 *    citations set aside. Anything less is a search that did not look properly,
 *    and an absence it reports is not one.
 *  - A reported result's source is not prior art. The model files a study it
 *    recalls as `evidence`, and a check that finds that very study has found the
 *    result's source — reading it as "somebody said this first" penalised a claim
 *    for citing its own paper accurately. So an evidence node gets the source
 *    line, never a re-rating, and never "nothing close".
 *  - A conjecture and what it departs from get no line at all (D288). A
 *    conjecture's prior-art line is its prior-art search, which reads its own
 *    null (`core/priorArt.ts`); "Nothing close in this search" beside a proposal
 *    nobody has made is the originality reading invariant 12 forbids, printed
 *    next to the search that exists to earn or refuse it — where both lines have
 *    rules, both hold (D284). And the expectation a conjecture departs from,
 *    carried onto the graph as an evidence node, is no reported result with a
 *    source and has no novelty of its own to re-rate: a page stating it is the
 *    consensus stated, which its check's reading already says.
 *
 * Pure, and read off stored fields only.
 */

import { liveOriginUrl } from './sources'
import { canonicalUrl } from './url'
import { fmtRating, selfRatingsOf } from './rewrite'
import { CLOSENESS_WORD } from './vocabulary'
import {
  passageRef,
  reportedResult,
  verificationLive,
  type PriorArt,
  type ReasonNode,
  type Verification,
} from './types'

// How close the work a check found sits to the claim is a status word, so
// `CLOSENESS_WORD` is defined in core/vocabulary.ts, with every other one.

/** Closest first. The model's own label, used only to pick which one to name. */
const CLOSENESS_RANK: Record<PriorArt['closeness'], number> = {
  same: 0,
  overlapping: 1,
  adjacent: 2,
}

/**
 * What a prior-art entry names in its own record. `passage` is present only on a
 * cited passage; `url` is absent on a passage of a document you added yourself,
 * which has nowhere to open.
 */
export interface PriorArtTarget {
  title: string
  url?: string
  passage?: number
}

export type PriorArtLine =
  | ({
      kind: 'prior'
      closeness: PriorArt['closeness']
      /** Present only where the checking model re-rated. `from` is absent on a
       *  claim the user rewrote: the self-rating described wording that is gone. */
      rerated?: { from?: number; to: number }
    } & PriorArtTarget)
  | ({ kind: 'source'; closeness: PriorArt['closeness'] } & PriorArtTarget)
  | { kind: 'nothing-close' }

/**
 * The kept web source or cited passage a prior-art entry names, or null where it
 * names neither.
 *
 * By equality with the key each gate stored, never by reading the ref: a web ref
 * is `canonicalUrl` of a kept source and a passage ref is `passageRef` of a cite,
 * so a ref naming nothing in THIS record — a legacy `p3` included — resolves to
 * nothing. Exported for `recordDigest.ts`, which names the same work in prose and
 * must not resolve it a second way.
 */
export function priorArtTarget(v: Verification, a: PriorArt): PriorArtTarget | null {
  const src = v.sources.find((x) => canonicalUrl(x.url) === a.ref)
  if (src) return { title: src.title, url: src.url }
  const cite = v.corpus?.cites.find((c) => passageRef(c) === a.ref)
  if (!cite) return null
  // A fetched record's door, the one its cite row already opens. Null for your
  // own document and for an id the derivation refuses, and then it is plain text.
  const url = liveOriginUrl(cite.docId)
  return { title: cite.docTitle, ...(url ? { url } : {}), passage: cite.passage }
}

/**
 * A web check that looked properly: it searched the web, results came back,
 * nothing failed, it was not cut off, and none of what it cited was set aside.
 * `not-checkable` and `unsourced` are not searches that found nothing: one says
 * sources cannot settle the claim, or that an absence stood beside a failed search
 * (D326), and the other lost every citation to the gate.
 */
export function completeWebCheck(v: Verification): boolean {
  return (
    v.scope !== 'corpus' &&
    v.seen > 0 &&
    !v.partial &&
    !v.failures?.length &&
    v.dropped === 0 &&
    v.reading !== 'not-checkable' &&
    v.reading !== 'unsourced'
  )
}

/** What the live check on a node says about prior work, or null for nothing. */
export function priorArtOf(n: ReasonNode): PriorArtLine | null {
  // Before anything is read: see the header. A conjecture's line is its search's.
  if (n.kind === 'conjecture' || n.carriedFrom !== undefined) return null
  const v = n.verification
  if (!v || !verificationLive(n)) return null
  const named = v.priorArt
    .map((a, i) => ({ a, i, src: priorArtTarget(v, a) }))
    .filter((x): x is { a: PriorArt; i: number; src: PriorArtTarget } => !!x.src)
    .sort((x, y) => CLOSENESS_RANK[x.a.closeness] - CLOSENESS_RANK[y.a.closeness] || x.i - y.i)
  const closest = named[0]
  if (reportedResult(n)) {
    return closest ? { kind: 'source', ...closest.src, closeness: closest.a.closeness } : null
  }
  if (closest) {
    // The model's own figure is the "from" — absent on a rewritten claim, whose
    // stored novelty rated the words it replaced.
    const rated = selfRatingsOf(n)
    return {
      kind: 'prior',
      ...closest.src,
      closeness: closest.a.closeness,
      ...(v.measuredNovelty !== undefined
        ? {
            rerated: {
              ...(rated ? { from: rated.novelty } : {}),
              to: v.measuredNovelty,
            },
          }
        : {}),
    }
  }
  // Prior work was named and none of it could be titled: say nothing rather than
  // "nothing close", which would be the opposite of what the check found.
  if (v.priorArt.length) return null
  return completeWebCheck(v) ? { kind: 'nothing-close' } : null
}

/** The checking model's re-rating, where a live check made one against prior
 *  work it could name — never on a reported result. */
export function measuredNoveltyOf(n: ReasonNode): number | undefined {
  const line = priorArtOf(n)
  return line?.kind === 'prior' ? line.rerated?.to : undefined
}

/** Outside the title, so a link names the document and the number stays text. */
const passageWords = (t: PriorArtTarget): string =>
  t.passage !== undefined ? `, passage ${t.passage}` : ''

/** The line in parts, so a surface can link the title. `title` is absent only on
 *  "nothing close". */
export function priorArtParts(line: PriorArtLine): {
  before: string
  title?: string
  url?: string
  after: string
} {
  switch (line.kind) {
    case 'nothing-close':
      return { before: 'Nothing close in this search', after: '' }
    case 'source':
      return {
        before: 'The check found this result’s source: ',
        title: line.title,
        ...(line.url ? { url: line.url } : {}),
        after: `${passageWords(line)} (${CLOSENESS_WORD[line.closeness]})`,
      }
    case 'prior': {
      const r = line.rerated
      const rating = !r
        ? ' · novelty not re-rated by the checking model'
        : r.from !== undefined
          ? ` · novelty re-rated ${fmtRating(r.from)} → ${fmtRating(r.to)} by the checking model`
          : ` · novelty rated ${fmtRating(r.to)} by the checking model`
      return {
        before: 'Closest prior work: ',
        title: line.title,
        ...(line.url ? { url: line.url } : {}),
        after: `${passageWords(line)} (${CLOSENESS_WORD[line.closeness]})${rating}`,
      }
    }
  }
}

/** The line as one sentence, for a surface that cannot link. */
export function priorArtText(line: PriorArtLine): string {
  const p = priorArtParts(line)
  return `${p.before}${p.title ?? ''}${p.after}`
}
